import { useState } from 'react';
import Modal from './Modal';
import { useAuth } from '../context/AuthContext';
import { createOrgUser, updateOrgUser } from '../api/client';
import './UserFormModal.css';

export default function UserFormModal({ user, onClose, onSaved }) {
  const { user: currentUser } = useAuth();
  const isEdit = Boolean(user);
  const isSelf = isEdit && user.id === currentUser.id;
  const [form, setForm] = useState({
    fullname: user?.fullname || '',
    username: user?.username || '',
    email: user?.email || '',
    role: user?.role || 2,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    const fullname = form.fullname.trim();
    const username = form.username.trim();
    const email = form.email.trim();
    if (!fullname || !username || !email) {
      setError('Full name, username, and email are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data = isEdit
        ? await updateOrgUser(user.id, { fullname, username, email, role: form.role })
        : await createOrgUser({ fullname, username, email, role: form.role });
      onSaved(data.user, { created: !isEdit });
    } catch (err) {
      setError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isEdit ? 'Edit user' : 'Add user'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <label className="user-form-field">
          <span>Full name</span>
          <input
            value={form.fullname}
            onChange={(e) => setForm((f) => ({ ...f, fullname: e.target.value }))}
            disabled={saving}
            autoFocus
          />
        </label>
        <label className="user-form-field">
          <span>Username</span>
          <input
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            disabled={saving}
          />
        </label>
        <label className="user-form-field">
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            disabled={saving}
          />
        </label>
        <label className="user-form-field">
          <span>Role</span>
          <select
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: Number(e.target.value) }))}
            disabled={saving || isSelf}
          >
            <option value={1}>Admin</option>
            <option value={2}>User</option>
          </select>
        </label>
        {isSelf && <p className="user-form-hint">You can't change your own role.</p>}

        {!isEdit && (
          <p className="user-form-hint">
            We'll generate a temporary password and email the login details to this address.
          </p>
        )}

        {error && <p className="user-form-error">{error}</p>}

        <div className="user-form-actions">
          <button type="button" className="user-form-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="user-form-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
